import { useTranslation } from 'react-i18next';
import BulletinSection from '@/components/common/BulletinSection';
import OrderFileLinks from '@/components/common/OrderFileLinks';
import TermList, { type TermItem } from '@/components/common/TermList';
import { loadingConditionRows } from '@/components/app/sell/LoadingConditionsSummary';
import type { LoadingConditions, SaleOrder } from '@/types/sell';
import classes from './SaleOfferTerms.module.css';

const CREDIT_TYPE_KEYS: Readonly<Record<string, string>> = {
  BY_AVERAGE_VALUE: 'app.sell.terms.creditAverage',
  BY_EACH_DAY: 'app.sell.terms.creditEachDay',
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Условия продавца, общие для его предложения и для встречного предложения покупателю. */
export interface SellerConditions {
  readonly loadingConditions?: LoadingConditions;
  readonly declarationNo?: string;
  readonly declarationDate?: string;
  readonly declarationFiles?: readonly { readonly id: number; readonly name?: string }[];
}

/** Скачивание файлов декларации: обработчик и файл, который сейчас скачивается. */
export interface FileDownload {
  readonly onDownload: (fileId: number) => void;
  readonly downloadingId?: number;
}

/** Строки условий продавца: погрузка, хранение, декларация с файлами. Пустые строки не выводятся. */
export function sellerConditionRows(terms: SellerConditions, t: Translate, download: FileDownload): TermItem[] {
  const lc = terms.loadingConditions;
  const files = terms.declarationFiles ?? [];
  return [
    ...(lc ? loadingConditionRows(lc, t) : []),
    ...(lc
      ? [
          {
            key: 'storage',
            label: t('app.sell.terms.storage'),
            value: lc.storageAvailable
              ? t('app.sell.terms.storageAvailable', { price: lc.storagePrice ?? 0 })
              : t('app.sell.terms.storageUrgent'),
          },
        ]
      : []),
    ...(terms.declarationNo || files.length > 0
      ? [
          {
            key: 'declaration',
            label: t('app.sell.terms.declaration'),
            value: (
              <>
                {terms.declarationNo ? (
                  <div>
                    № {terms.declarationNo}
                    {terms.declarationDate ? ` · ${terms.declarationDate}` : ''}
                  </div>
                ) : null}
                {/* Кнопка скачивания стоит рядом с именем файла, а не у дальнего края широкой строки. */}
                <div className={classes.files}>
                  <OrderFileLinks files={files} onDownload={download.onDownload} downloadingId={download.downloadingId} />
                </div>
              </>
            ),
          },
        ]
      : []),
  ];
}

interface SaleOfferTermsProps extends FileDownload {
  readonly offer: SaleOrder;
}

// «Условия предложения» продавца («Бюллетень»): зачёт качества, приёмка, условия погрузки, хранение, декларация
// с файлами, комментарий. Данные — из самого предложения, дополнительных запросов нет. Пустые строки не выводятся.
export default function SaleOfferTerms({ offer, onDownload, downloadingId }: SaleOfferTermsProps) {
  const { t } = useTranslation();
  const creditKey = offer.qualityCreditType ? CREDIT_TYPE_KEYS[offer.qualityCreditType] : undefined;

  const rows: TermItem[] = [
    ...(offer.qualityCreditType
      ? [{ key: 'credit', label: t('app.sell.terms.credit'), value: creditKey ? t(creditKey) : offer.qualityCreditType }]
      : []),
    {
      key: 'acceptance',
      label: t('app.sell.terms.acceptance'),
      value: t(offer.onlyByGOST ? 'app.sell.terms.acceptanceGostOnly' : 'app.sell.terms.acceptanceFlexible'),
    },
    ...sellerConditionRows(offer, t, { onDownload, downloadingId }),
    ...(offer.userComment
      ? [{ key: 'comment', label: t('app.sell.terms.comment'), value: <span className={classes.comment}>{offer.userComment}</span> }]
      : []),
  ];

  return (
    <BulletinSection title={t('app.sell.terms.title')}>
      <TermList items={rows} />
    </BulletinSection>
  );
}
